import { Button } from '@kerfjs/ui/button';
import { Checkbox } from '@kerfjs/ui/checkbox';
import { List } from '@kerfjs/ui/list';
import { ListHeader } from '@kerfjs/ui/list-header';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { Spacer } from '@kerfjs/ui/spacer';
import { StateBanner } from '@kerfjs/ui/state-banner';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import { attr, type SafeHtml } from 'kerfjs';

import { MEMBER_ROLES } from '../../lib/member-roles.js';

export const MEMBER_INVITE_DIALOG_ACTIONS = {
  dialog: attr('data-action', 'member-invite-dialog'),
  cancel: attr('data-action', 'member-dialog-cancel'),
  send: attr('data-action', 'member-invite'),
} as const;

/**
 * Invite a member by email with one or more roles (HS2-FJ9J2K). The invitation is
 * emailed and becomes active when that person signs in with the address. Rendered
 * open by the Members page while the invite dialog state is set; a failed submit
 * shows its message in the dialog.
 */
export function MemberInviteDialog({ error, organizationName }: { error: string; organizationName: string }): SafeHtml {
  return (
    <DialogSurface
      label="Invite Member"
      lightDismiss
      className="member-invite-dialog"
      rootAttributes={{ 'data-action': MEMBER_INVITE_DIALOG_ACTIONS.dialog.value, 'data-auto-open-dialog': '' }}
      footer={
        <>
          <Button
            size="default"
            tone="neutral"
            emphasis="plain"
            action={MEMBER_INVITE_DIALOG_ACTIONS.cancel.value}
            rootAttributes={{ 'data-dialog-close': '' }}
          >
            Cancel
          </Button>
          <Spacer flex />
          <Button size="default" tone="brand" emphasis="primary" action={MEMBER_INVITE_DIALOG_ACTIONS.send.value}>
            Send Invitation
          </Button>
        </>
      }
    >
      <List gap="m">
        {error === '' ? null : <StateBanner tone="danger" title={error} />}
        <ListInsetControl sides="rl">
          <Text size="compact" tone="quiet">
            You are inviting this person to <strong>{organizationName}</strong>. We email the invitation; it becomes
            active when this person signs in with that email.
          </Text>
        </ListInsetControl>
        <ListInsetControl>
          <TextField name="member-invite-email" type="email" label="Email Address" required autofocus />
        </ListInsetControl>
        <div role="group" aria-label="Roles">
          <List gap="xs">
            <ListHeader label="Roles" size="xs" density="compact" />
            <ListInsetControl>
              {/* One role per line, matching the member dialog (HS2-MM9A1R, HS2-XF4W1Z). */}
              <List gap="xs">
                {MEMBER_ROLES.map((role) => (
                  <Checkbox name="member-invite-role" value={role} checked={role === 'requester'} label={role} />
                ))}
              </List>
            </ListInsetControl>
          </List>
        </div>
      </List>
    </DialogSurface>
  );
}
